import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Divider
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const IncentiveForm = () => {
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [form, setForm] = useState({
    branchId: '',
    employeeId: '',
    amount: '',
    month: '',
    reason: ''
  });

  useEffect(() => {
    api.get('/branches')
      .then(res => setBranches(res.data))
      .catch(() => toast.error('Failed to load branches'));
  }, []);

  useEffect(() => {
    if (!form.branchId) {
      setEmployees([]);
      return;
    }
    setLoadingEmployees(true);
    api.get(`/employees/branch/${form.branchId}`)
      .then(res => setEmployees(res.data))
      .catch(() => toast.error('Failed to load employees for this branch'))
      .finally(() => setLoadingEmployees(false));
  }, [form.branchId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'branchId' ? { employeeId: '' } : {})
    }));
  };

  const handleSubmit = async () => {
    const { employeeId, amount, month, reason, branchId } = form;

    if (!branchId) {
      toast.error('Please select a branch first');
      return;
    }
    if (!employeeId || !amount || !month || !reason) {
      toast.error('All fields are required');
      return;
    }

    try {
      await api.post('/incentives', {
        employeeId: Number(employeeId),
        amount: Number(amount),
        month,
        reason
      });

      toast.success('Incentive added successfully');

      setForm({
        branchId,
        employeeId: '',
        amount: '',
        month: '',
        reason: ''
      });
    } catch (err) {
      toast.error('Failed to create incentive');
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight={800} mb={1}>
        Incentive Management
      </Typography>
      <Typography color="text.secondary" mb={3}>
        Add monthly incentives for employees
      </Typography>

      <Paper sx={{ p: 4, maxWidth: 600, borderRadius: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <PaymentsIcon color="success" />
          <Typography fontWeight={700}>New Incentive</Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Branch"
              name="branchId"
              value={form.branchId}
              onChange={handleChange}
            >
              {branches.map(b => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Employee"
              name="employeeId"
              value={form.employeeId}
              onChange={handleChange}
              disabled={!form.branchId || loadingEmployees}
              helperText={!form.branchId ? 'Select a branch first' : ''}
            >
              {employees.map(e => (
                <MenuItem key={e.id} value={e.id}>
                  {e.name} ({e.employeeCode})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Incentive Amount (₹)"
              name="amount"
              value={form.amount}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="month"
              label="Applicable Month"
              name="month"
              InputLabelProps={{ shrink: true }}
              value={form.month}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason"
              name="reason"
              value={form.reason}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              fullWidth
              size="large"
              variant="contained"
              color="success"
              onClick={handleSubmit}
            >
              Save Incentive
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default IncentiveForm;